import { useWidgetConfigForRole } from '@/hooks/use-widget-configs';
import { useUserWidgetOverrides } from '@/hooks/use-user-widget-overrides';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  filterWidgetsByMenuAccess,
  getV2WidgetsForRole,
  getWidgetsForRole,
  mergeWidgetOverrides,
} from '@/lib/widget-registry';
import { useCanAccessMenu } from '@/hooks/use-menu-permissions';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import WidgetRenderer from './widgets/WidgetRenderer';
import MaaltallAgentPanel from './widgets/MaaltallAgentPanel';
import BirthdayBanner from './BirthdayBanner';
import { SrIcon } from '@/components/ui/SrIcon';
import { cn } from '@/lib/utils';

interface WidgetDashboardProps {
  role: string;
  greeting?: string;
}

function getIsoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

function formatEyebrow(now: Date): string {
  const month = now.toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });
  const week = getIsoWeek(now);
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} · uke ${week}`;
}

export default function WidgetDashboard({ role, greeting }: WidgetDashboardProps) {
  const { activeUser } = useImpersonation();
  const { data: dbWidgets, isLoading } = useWidgetConfigForRole(role);
  const { data: overrides = [] } = useUserWidgetOverrides(activeUser?.id);
  const salesUnitHourlyRateEnabled = useCanAccessMenu('timepris-selger');
  const projectLeadHourlyRateEnabled = useCanAccessMenu('timepris-prosjektleder');

  const maaltallV2 = useFeatureFlag('feature_maaltall_v2');
  const agentEnabled = useFeatureFlag('feature_maaltall_agent');
  const showAgent = maaltallV2 && agentEnabled && (role === 'admin' || role === 'superadmin');

  const baseWidgets = maaltallV2 ? getV2WidgetsForRole(role) : dbWidgets ?? getWidgetsForRole(role);
  const merged = mergeWidgetOverrides(
    baseWidgets,
    overrides.map((o) => ({
      widget_id: o.widget_id,
      visible: o.visible,
      display_order: o.display_order,
    })),
  );
  const sorted = filterWidgetsByMenuAccess(merged, {
    'timepris-selger': salesUnitHourlyRateEnabled,
    'timepris-prosjektleder': projectLeadHourlyRateEnabled,
  }).sort((a, b) => a.order - b.order);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 rounded-full border-2 border-[var(--sr-revemoerk)] border-t-transparent animate-spin" />
      </div>
    );
  }

  const now = new Date();

  return (
    <div className="page-container animate-fade-in space-y-6">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-foreground/60 mb-2">
            {formatEyebrow(now)}
          </div>
          <h1 className="relative inline-block font-display font-semibold text-[40px] leading-[1.05] tracking-[-0.015em] text-foreground">
            {greeting ?? 'Måltall'}
            <span
              aria-hidden
              className="absolute left-0 -bottom-2.5 w-full text-[var(--sr-reveblaa)]"
              style={{ height: 10 }}
            >
              <SrIcon name="scribble-underline" size="100%" className="w-full h-full" />
            </span>
          </h1>
        </div>
        {showAgent && <MaaltallAgentPanel />}
      </header>

      <BirthdayBanner />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sorted.map((w) => [
          w.section && (
            <h2
              key={`section-${w.key}`}
              className="md:col-span-2 mt-3 -mb-1 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[#8A97A3]"
            >
              {w.section}
            </h2>
          ),
          <div
            key={w.key}
            id={`widget-${w.key}`}
            className={cn(
              'scroll-mt-6',
              // Widgets som ikke gjelder brukeren rendrer null. Da skjules
              // cellen, så den ikke lager et hull i gridet.
              'empty:hidden',
              w.size === 'full' && 'md:col-span-2',
              w.size === 'third' && 'md:col-span-1',
            )}
          >
            <WidgetRenderer widgetKey={w.key} />
          </div>,
        ])}
      </div>
    </div>
  );
}
